"use client";
import useMediaGetter from "@/app/hooks/useMediaGetter";
import Banner from "../shared/Banner";
import { useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import { screenSizeContext } from "@/app/store/context/screenSizesContext";
import { loadingContext } from "@/app/store/context/isLoadingContext";
import { useRouter } from "next/navigation";

const MasterPlan = dynamic(() => import("./MasterPlan"), {
	ssr: false,
	loading: () => (
		<div className="min-h-[50vh] lg:min-h-screen bg-oBlue-100" aria-hidden />
	),
});

const GetInTouch = dynamic(() => import("../shared/form/GetInTouch"), {
	loading: () => (
		<div id="get_in_touch" className="min-h-[32rem] bg-white" aria-hidden />
	),
});

const NeighborhoodsPageComponents = ({
	data,
	masterplanData,
	destinationData,
}: any) => {
	const { isPageFetched, setIsPageFetched } = useContext(loadingContext);
	const { isIpad, isReady } = useContext(screenSizeContext);

	const router = useRouter();
	const params = useSearchParams();
	const booking = params.get("book");
	const { imgSrc } = useMediaGetter(data?.banners?.[0]?.media);
	const [masterplanSelectedNeighborhood, setMasterplanSelectedNeighborhood] =
		useState<string>("");

	const removeParam = (paramName: string) => {
		const p = new URLSearchParams(params.toString());
		p.delete(paramName);

		const newUrl = p.toString() ? `?${p.toString()}` : "";
		router.push(`${window.location.pathname}${newUrl}`, { scroll: false });
	};

	useEffect(() => {
		if (!isReady || !booking || isPageFetched !== "fetched") return;

			const html = document.documentElement;

			const timer = setTimeout(async () => {
				const form = document.getElementById("get_in_touch") as HTMLFormElement;
				if (!form) return;

				if (!isIpad) {
					window.lenis?.scrollTo(form.offsetTop, {
						force: true,
						duration: 2,
						easing: (x) =>
							x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2,
						onComplete() {
							window.lenis?.startScroll?.(true);
							removeParam("book");
						},
					});
				} else {
					document.documentElement.style.overflow = "auto";
					const { default: gsap } = await import("gsap");
					const { ScrollToPlugin } = await import("gsap/ScrollToPlugin");
					gsap.registerPlugin(ScrollToPlugin);
					gsap.to(window, { scrollTo: { y: form.offsetTop } });
					removeParam("book");
				}
			}, html.classList.contains("loaded") ? 2300 : 4500);

			return () => clearTimeout(timer);
	}, [booking, isPageFetched, isIpad, isReady, params, router]);

	useEffect(() => {
		if (imgSrc) {
			setTimeout(() => {
				setIsPageFetched((prev: string) => {
					if (prev === "loading") return "fetched";
					return prev;
				});
			});
		}
	}, [imgSrc, setIsPageFetched]);

	const tl = useRef<GSAPTimeline | null>(null);

	useEffect(() => {
		if (isPageFetched === "fetched" && imgSrc) {
			let cancelled = false;

			const timer = setTimeout(async () => {
				const { default: gsap } = await import("gsap");
				if (cancelled) return;

				const html = document.documentElement;

				tl.current?.kill();
				tl.current = gsap.timeline();
				tl.current
					.timeScale(html.classList.contains("loaded") ? 1.25 : 1)
					.set(".banner .hero-title-word", { yPercent: 50, alpha: 0 })
					.set("#masterplan > div", { yPercent: 30 })
					.to(".banner .hero-image img", {
						delay: html.classList.contains("loaded") ? 0.4 : 2,
						duration: 1,
						ease: "power2.inOut",
						y: "0%",
					})
					.to(
						".banner .hero-image-wrapper",
						{
							duration: 1,
							ease: "power2.inOut",
							scale: 1,
						},
						"-=0.25"
					)
					.to(
						".navbar_section",
						{
							duration: 1,
							ease: "power2.inOut",
							y: "0%",
						},
						"-=0.4"
					)
					.to(
						".banner .hero-title-word",
						{
							duration: 1,
							stagger: 0.05,
							ease: "expo.inOut",
							yPercent: 0,
							alpha: 1,
						},
						"<"
					)
					.to(
						"#masterplan > div",
						{
							duration: 1,
							stagger: 0.05,
							ease: "expo.out",
							yPercent: 0,
						},
						"<"
					);
			}, 50);

			return () => {
				cancelled = true;
				clearTimeout(timer);
				tl.current?.kill();
			};
		}
	}, [isPageFetched, imgSrc]);

	return (
		<div className={`page_wrapper ${isIpad && "neighborhoods-page"}`}>
			{imgSrc ? (
				<Banner
					priority
					imageSrc={`${imgSrc}`}
					minHeight="75vh"
					mobileHeight="50vh"
					title={data?.banners[0].title.en}
					description={data?.banners[0].description.en}
					neighborhoodsPage
					optimizedImage
				/>
			) : (
				<div className="lg:h-[75vh]"></div>
			)}
			<section id="masterplan" className="lg:h-screen">
				<MasterPlan
					data={masterplanData}
					title={masterplanData?.title}
					setMasterplanSelectedNeighborhood={setMasterplanSelectedNeighborhood}
					optimizedImage
				/>
			</section>
			<GetInTouch
				destinationData={destinationData}
				masterplanSelectedNeighborhood={masterplanSelectedNeighborhood}
				setMasterplanSelectedNeighborhood={setMasterplanSelectedNeighborhood}
				NeighborhoodsPage
			/>
		</div>
	);
};

export default NeighborhoodsPageComponents;
